export const RECOVERABLE_COLLECTION_NAMES = [
  "epocus-billing",
  "epocus-billing-status",
  "epocus-consent-document",
  "epocus-encounter",
  "epocus-exam",
  "epocus-patient-consent",
  "epocus-probe-association",
  "epocus-program",
  "epocus-review",
  "imaging-instance",
  "imaging-study",
  "sync-checkpoint",
] as const;

export const LOCAL_ONLY_COLLECTION_NAMES = [
  "epocus-encounter-patient",
  "epocus-referral",
  "epocus-referral-outcome",
] as const;

const EARLIEST_NON_PUSHED_CHANGE_AT_PREFIX = "earliestNonPushedChangeAt-";

const isRecordedChange = (rawValue: string | null): boolean => {
  if (rawValue === null) return false;
  try {
    return JSON.parse(rawValue) !== null;
  } catch {
    return false;
  }
};

const hasUnpushedReplicatedWrites = (): boolean =>
  Object.keys(localStorage)
    .filter((key) => key.startsWith(EARLIEST_NON_PUSHED_CHANGE_AT_PREFIX))
    .some((key) => isRecordedChange(localStorage.getItem(key)));

// A row counts as live unless it is definitely deleted: the Dexie adapter
// writes `_deleted` as the string "1", so a boolean check reads every live row
// as deleted, and anything unrecognised must err towards keeping the data.
const isDeleted = (document: { _deleted?: unknown }): boolean =>
  document._deleted === "1" ||
  document._deleted === true ||
  document._deleted === 1;

const storesRows = (indexedDatabaseName: string) =>
  new Promise<boolean>((resolve) => {
    const request = indexedDB.open(indexedDatabaseName);
    request.onerror = () => resolve(true);
    request.onsuccess = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains("docs")) {
        database.close();
        resolve(false);
        return;
      }
      const cursor = database
        .transaction(["docs"], "readonly")
        .objectStore("docs")
        .openCursor();
      cursor.onerror = () => {
        database.close();
        resolve(true);
      };
      cursor.onsuccess = () => {
        const position = cursor.result;
        if (!position) {
          database.close();
          resolve(false);
          return;
        }
        if (!isDeleted(position.value)) {
          database.close();
          resolve(true);
          return;
        }
        position.continue();
      };
    };
  });

const localOnlyDatabaseNames = (databaseName: string, existing: string[]) =>
  existing.filter((name) =>
    LOCAL_ONLY_COLLECTION_NAMES.some((collection) =>
      new RegExp(`^rxdb-dexie-${databaseName}--\\d+--${collection}$`).test(
        name,
      ),
    ),
  );

const holdsLocalOnlyRows = async (databaseName: string): Promise<boolean> => {
  if (typeof indexedDB.databases !== "function") return true;

  const existing = (await indexedDB.databases())
    .map(({ name }) => name)
    .filter((name): name is string => typeof name === "string");

  const results = await Promise.all(
    localOnlyDatabaseNames(databaseName, existing).map(storesRows),
  );
  return results.some(Boolean);
};

export const hasUnsyncedLocalData = async (
  databaseName: string,
): Promise<boolean> =>
  hasUnpushedReplicatedWrites() || holdsLocalOnlyRows(databaseName);
